import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { ProjectProvider } from "@/contexts/ProjectContext";
import { TimeBarProvider } from "@/contexts/TimeBarContext";
import { PermissionProvider } from "@/contexts/PermissionContext";
import { SafetyAlertProvider } from "@/contexts/SafetyAlertContext";
import { Toaster } from "sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import LandingPage from "@/pages/LandingPage";
import AcceptInvite from "@/pages/AcceptInvite";
import AuthCallback from "@/pages/AuthCallback";
import TrialExpired from "@/pages/TrialExpired";
import MyOrganization from "@/pages/MyOrganization";
import PortfolioAnalyticsDashboard from "@/pages/PortfolioAnalyticsDashboard";
import AIAnalystHub from "@/pages/AIAnalystHub";
import CEOBriefing from "@/pages/CEOBriefing";
import ProjectsList from "@/pages/ProjectsList";
import ProjectDashboard from "@/pages/ProjectDashboard";
import NfaManagement from "@/pages/NfaManagement";
import Settings from "@/pages/Settings";
import OrganizationSetup from "@/pages/OrganizationSetup";
import UserManagement from "@/pages/UserManagement";
import ProjectSelector from "@/pages/ProjectSelector";
import ModuleLauncher from "@/pages/ModuleLauncher";
import ProjectCreationWizard from "@/pages/ProjectCreationWizard";
import UsersAccessManagement from "@/pages/UsersAccessManagement";
import SchedulingProjectsList from "@/pages/SchedulingProjectsList";
import SchedulingDashboardEnhanced from "@/pages/SchedulingDashboardEnhanced";
import EdmDocumentDetail from "@/pages/EdmDocumentDetail";
import EdmRevisionViewer from "@/pages/EdmRevisionViewer";
import EdmManagement from "@/pages/EdmManagement";
import InterfaceManagement from "@/pages/InterfaceManagement";
import RFIDetailPage from "@/pages/RFIDetailPage";
import ContractsModule from "@/pages/ContractsModule";
import ContractsPortfolio from "@/pages/ContractsPortfolio";
import ContractsCorrespondence from "@/pages/ContractsCorrespondence";
import ContractsLDExposure from "@/pages/ContractsLDExposure";
import ContractsClaimsWorkspace from "@/pages/ContractsClaimsWorkspace";
import ContractsVariations from "@/pages/ContractsVariations";
import ContractsObligations from "@/pages/ContractsObligations";
import ContractsPayments from "@/pages/ContractsPayments";
import ContractsCompare from "@/pages/ContractsCompare";
import ContractDetail from "@/pages/ContractDetail";
import TenderAnalytics from "@/pages/TenderAnalytics";
import TenderDetail from "@/pages/TenderDetail";
import ProjectControls from "@/pages/ProjectControls";
import EngineeringSchedule from "@/pages/EngineeringSchedule";
import ProcurementSchedule from "@/pages/ProcurementSchedule";
import ConstructionSchedule from "@/pages/ConstructionSchedule";
import DailyDPRCapture from "@/pages/DailyDPRCapture";
import CommissioningSchedule from "@/pages/CommissioningSchedule";
import PipingWorkfront from "@/pages/PipingWorkfront";
import StructuralWorkfront from "@/pages/StructuralWorkfront";
import EngineeringAnalytics from "@/pages/EngineeringAnalytics";
import InvoiceManagement from "@/pages/InvoiceManagement";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import WorkingCapitalManagement from "@/pages/WorkingCapitalManagement";
import EarnedValueManagement from "@/pages/EarnedValueManagement";
import CostManagementSummary from "@/pages/CostManagementSummary";
import BudgetRevisionTracker from "@/pages/BudgetRevisionTracker";
import CommitmentTracking from "@/pages/CommitmentTracking";
import CBSMatrix from "@/pages/CBSMatrix";
import ManpowerManagement from "@/pages/ManpowerManagement";
import EquipmentManagement from "@/pages/EquipmentManagement";
import FourDModelling from "@/pages/FourDModelling";
import FiveDModelling from "@/pages/FiveDModelling";
import HSEDashboard from "@/pages/HSEDashboard";
import RAIDDashboard from "@/pages/RAIDDashboard";
import SiteMonitoring from "@/pages/SiteMonitoring";
import ScheduleRollUp from "@/pages/ScheduleRollUp";
import ScanToModel from "@/pages/ScanToModel";
import ProgressReports from "@/pages/ProgressReports";
import ReportsWizardV2 from "@/pages/ReportsWizardV2";
import MaterialTrackingHub from "@/pages/MaterialTrackingHub";
import MobileScan from "@/pages/MobileScan";
import SettingsNotifications from "@/pages/SettingsNotifications";
import SettingsConnectors from "@/pages/SettingsConnectors";

// Admin Portal pages
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminTrialSignups from "@/pages/admin/AdminTrialSignups";
import AdminUsers from "@/pages/admin/AdminUsers";
import DataConnectorsAdmin from "@/pages/admin/DataConnectorsAdmin";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminModules from "@/pages/admin/AdminModules";
import AdminRoleTemplates from "@/pages/admin/AdminRoleTemplates";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminInvitations from "@/pages/admin/AdminInvitations";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminFeatureFlags from "@/pages/admin/AdminFeatureFlags";
import AdminLicense from "@/pages/admin/AdminLicense";
import AdminLicences from "@/pages/admin/AdminLicences";
import AdminCloudDeploy from "@/pages/admin/AdminCloudDeploy";
import AdminSelfMigrate from "@/pages/admin/AdminSelfMigrate";
import AdminAiEngine from "@/pages/admin/AdminAiEngine";
import LicenceCollect from "@/pages/licences/LicenceCollect";
import LicenceView from "@/pages/licences/LicenceView";

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/trial-expired" element={<TrialExpired />} />
      <Route path="/setup" element={<OrganizationSetup />} />
      <Route path="/project-selector" element={<ProtectedRoute><ProjectSelector /></ProtectedRoute>} />
      <Route path="/launcher" element={<ProtectedRoute><ModuleLauncher /></ProtectedRoute>} />
      <Route path="/projects/create" element={<ProtectedRoute><ProjectCreationWizard /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsList /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDashboard /></ProtectedRoute>} />
      <Route path="/project-controls/nfa" element={<ProtectedRoute><NfaManagement /></ProtectedRoute>} />
      {/* Sunset: /dashboard was the old executive view. All bookmarks and
          deep-links are permanently redirected to the redesigned cockpit at
          /portfolio (SPA-side 301 equivalent via <Navigate replace/>). */}
      <Route path="/dashboard" element={<Navigate to="/launcher" replace />} />
      <Route path="/portfolio" element={<ProtectedRoute><PortfolioAnalyticsDashboard /></ProtectedRoute>} />
      {/* Project Controls Module */}
      <Route path="/project-controls" element={<ProtectedRoute><ProjectControls /></ProtectedRoute>} />
      <Route path="/project-controls/macro" element={<ProtectedRoute><SchedulingProjectsList /></ProtectedRoute>} />
      <Route path="/project-controls/macro/:scheduleProjectId" element={<ProtectedRoute><SchedulingDashboardEnhanced /></ProtectedRoute>} />
      {/* Document Management IS the EDM module. */}
      <Route path="/project-controls/dms" element={<ProtectedRoute><EdmManagement /></ProtectedRoute>} />
      {/* The legacy DMS page was retired in S8/W11. Its RFIs are an EDM tab, its
          submittals were migrated into the VDR, and its register is the EDM register.
          The old paths redirect rather than 404 — they are in people's bookmarks. */}
      <Route path="/project-controls/dms/legacy" element={<Navigate to="/project-controls/dms" replace />} />
      <Route path="/project-controls/dms/legacy/:projectId" element={<Navigate to="/project-controls/dms" replace />} />
      <Route path="/project-controls/dms/:projectId" element={<ProtectedRoute><EdmManagement /></ProtectedRoute>} />
      <Route path="/project-controls/dms/:projectId/rfis/:rfiId" element={<ProtectedRoute><RFIDetailPage /></ProtectedRoute>} />
      {/* Engineering Drawing & Document Management — register + document detail.
          The remaining screens land in P6. */}
      <Route path="/engineering/documents" element={<ProtectedRoute><EdmManagement /></ProtectedRoute>} />
      <Route path="/engineering/documents/:documentId" element={<ProtectedRoute><EdmDocumentDetail /></ProtectedRoute>} />
      <Route path="/engineering/revisions/:revisionId/viewer" element={<ProtectedRoute><EdmRevisionViewer /></ProtectedRoute>} />
      <Route path="/project-controls/engineering" element={<ProtectedRoute><EngineeringSchedule /></ProtectedRoute>} />
      <Route path="/project-controls/engineering/:projectId" element={<ProtectedRoute><EngineeringSchedule /></ProtectedRoute>} />
      <Route path="/project-controls/engineering/:projectId/analytics" element={<ProtectedRoute><EngineeringAnalytics /></ProtectedRoute>} />
      <Route path="/project-controls/procurement" element={<ProtectedRoute><ProcurementSchedule /></ProtectedRoute>} />
      <Route path="/project-controls/construction" element={<ProtectedRoute><ConstructionSchedule /></ProtectedRoute>} />
      <Route path="/project-controls/construction/dpr" element={<ProtectedRoute><DailyDPRCapture /></ProtectedRoute>} />
      <Route path="/project-controls/commissioning" element={<ProtectedRoute><CommissioningSchedule /></ProtectedRoute>} />
      <Route path="/project-controls/4d-model" element={<ProtectedRoute><FourDModelling /></ProtectedRoute>} />
      <Route path="/project-controls/interfaces" element={<ProtectedRoute><InterfaceManagement /></ProtectedRoute>} />
      {/* Cost Management */}
      <Route path="/project-controls/cost" element={<ProtectedRoute><CostManagementSummary /></ProtectedRoute>} />
      <Route path="/project-controls/cost/budget-revisions" element={<ProtectedRoute><BudgetRevisionTracker /></ProtectedRoute>} />
      <Route path="/project-controls/cost/commitments" element={<ProtectedRoute><CommitmentTracking /></ProtectedRoute>} />
      <Route path="/project-controls/cost/invoices" element={<ProtectedRoute><InvoiceManagement /></ProtectedRoute>} />
      <Route path="/project-controls/cost/invoices/:invoiceId" element={<ProtectedRoute><InvoiceDetailPage /></ProtectedRoute>} />
      <Route path="/project-controls/cost/working-capital" element={<ProtectedRoute><WorkingCapitalManagement /></ProtectedRoute>} />
      <Route path="/project-controls/cost/evm" element={<ProtectedRoute><EarnedValueManagement /></ProtectedRoute>} />
      <Route path="/project-controls/cost/cbs" element={<ProtectedRoute><CBSMatrix /></ProtectedRoute>} />
      <Route path="/project-controls/cost/manpower" element={<ProtectedRoute><ManpowerManagement /></ProtectedRoute>} />
      <Route path="/project-controls/cost/equipment" element={<ProtectedRoute><EquipmentManagement /></ProtectedRoute>} />
      <Route path="/project-controls/cost/5d-model" element={<ProtectedRoute><FiveDModelling /></ProtectedRoute>} />
      {/* HSE Management */}
      <Route path="/project-controls/hse" element={<ProtectedRoute><HSEDashboard /></ProtectedRoute>} />
      {/* RAID Log */}
      <Route path="/project-controls/raid" element={<ProtectedRoute><RAIDDashboard /></ProtectedRoute>} />
      <Route path="/ai-analyst" element={<ProtectedRoute><AIAnalystHub /></ProtectedRoute>} />
      <Route path="/ceo-briefing" element={<ProtectedRoute><CEOBriefing /></ProtectedRoute>} />
      <Route path="/project-controls/site-monitoring" element={<ProtectedRoute><SiteMonitoring /></ProtectedRoute>} />
      {/* Schedule Roll-Up */}
      <Route path="/project-controls/schedule-rollup" element={<ProtectedRoute><ScheduleRollUp /></ProtectedRoute>} />
      <Route path="/piping-workfront" element={<ProtectedRoute><PipingWorkfront /></ProtectedRoute>} />
      <Route path="/structural-workfront" element={<ProtectedRoute><StructuralWorkfront /></ProtectedRoute>} />
      <Route path="/material-tracking" element={<Navigate to="/project-controls/material-tracking" replace />} />
      <Route path="/project-controls/material-tracking" element={<ProtectedRoute><MaterialTrackingHub /></ProtectedRoute>} />
      <Route path="/mobile/scan" element={<MobileScan />} />
      <Route path="/project-controls/scan-to-model" element={<ProtectedRoute><ScanToModel /></ProtectedRoute>} />
      {/* Progress Reporting */}
      <Route path="/project-controls/progress-reports" element={<ProtectedRoute><ReportsWizardV2 /></ProtectedRoute>} />
      <Route path="/project-controls/progress-reports-legacy" element={<ProtectedRoute><ProgressReports /></ProtectedRoute>} />
      <Route path="/project-controls/progress-reports-v2" element={<ProtectedRoute><ReportsWizardV2 /></ProtectedRoute>} />
      {/* Legacy scheduling routes - redirect to new paths */}
      <Route path="/scheduling" element={<Navigate to="/project-controls/macro" replace />} />
      <Route path="/scheduling/:scheduleProjectId" element={<ProtectedRoute><SchedulingDashboardEnhanced /></ProtectedRoute>} />
      {/* Contracts & Tenders */}
      <Route path="/contracts" element={<ProtectedRoute><ContractsPortfolio /></ProtectedRoute>} />
      <Route path="/contracts/portfolio" element={<Navigate to="/contracts" replace />} />
      <Route path="/contracts/single-project" element={<ProtectedRoute><ContractsModule /></ProtectedRoute>} />
      <Route path="/contracts/correspondence" element={<ProtectedRoute><ContractsCorrespondence /></ProtectedRoute>} />
      <Route path="/contracts/ld-exposure" element={<ProtectedRoute><ContractsLDExposure /></ProtectedRoute>} />
      <Route path="/contracts/claims" element={<ProtectedRoute><ContractsClaimsWorkspace /></ProtectedRoute>} />
      <Route path="/contracts/variations" element={<ProtectedRoute><ContractsVariations /></ProtectedRoute>} />
      <Route path="/contracts/obligations" element={<ProtectedRoute><ContractsObligations /></ProtectedRoute>} />
      <Route path="/contracts/payments" element={<ProtectedRoute><ContractsPayments /></ProtectedRoute>} />
      <Route path="/contracts/compare" element={<ProtectedRoute><ContractsCompare /></ProtectedRoute>} />
      <Route path="/contracts/:contractId" element={<ProtectedRoute><ContractDetail /></ProtectedRoute>} />
      <Route path="/tenders" element={<ProtectedRoute><TenderAnalytics /></ProtectedRoute>} />
      <Route path="/tenders/:tenderId" element={<ProtectedRoute><TenderDetail /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><SettingsNotifications /></ProtectedRoute>} />
      <Route path="/settings/connectors" element={<ProtectedRoute><SettingsConnectors /></ProtectedRoute>} />
      {/* Legacy /notifications route — notifications now live under Settings */}
      <Route path="/notifications" element={<Navigate to="/settings/notifications" replace />} />
      <Route path="/users" element={<ProtectedRoute><UsersAccessManagement /></ProtectedRoute>} />
      <Route path="/org" element={<ProtectedRoute><MyOrganization /></ProtectedRoute>} />
      <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      {/* Admin Portal — PLATFORM_ADMIN only (gate inside AdminLayout) */}
      <Route path="/admin" element={<ProtectedRoute><AdminOverview /></ProtectedRoute>} />
      <Route path="/admin/organizations" element={<ProtectedRoute><AdminOrganizations /></ProtectedRoute>} />
      <Route path="/admin/trial-signups" element={<ProtectedRoute><AdminTrialSignups /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/projects" element={<ProtectedRoute><AdminProjects /></ProtectedRoute>} />
      <Route path="/admin/modules" element={<ProtectedRoute><AdminModules /></ProtectedRoute>} />
      <Route path="/admin/role-templates" element={<ProtectedRoute><AdminRoleTemplates /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute><AdminAudit /></ProtectedRoute>} />
      <Route path="/admin/invitations" element={<ProtectedRoute><AdminInvitations /></ProtectedRoute>} />
      <Route path="/admin/data-connectors" element={<ProtectedRoute><DataConnectorsAdmin /></ProtectedRoute>} />
      <Route path="/admin/integrations" element={<ProtectedRoute><AdminIntegrations /></ProtectedRoute>} />
      <Route path="/admin/feature-flags" element={<ProtectedRoute><AdminFeatureFlags /></ProtectedRoute>} />
      <Route path="/admin/license" element={<ProtectedRoute><AdminLicense /></ProtectedRoute>} />
      <Route path="/admin/licences" element={<ProtectedRoute><AdminLicences /></ProtectedRoute>} />
      <Route path="/admin/cloud-deploy" element={<ProtectedRoute><AdminCloudDeploy /></ProtectedRoute>} />
      <Route path="/admin/move-to-cloud" element={<ProtectedRoute><AdminSelfMigrate /></ProtectedRoute>} />
      <Route path="/admin/ai-engine" element={<ProtectedRoute><AdminAiEngine /></ProtectedRoute>} />
      {/* Public Licence Portal — magic-link redemption + reveal (unauthenticated) */}
      <Route path="/licences/collect/:token" element={<LicenceCollect />} />
      <Route path="/licences/view" element={<LicenceView />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedApp() {
  const { isDark } = useTheme();
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--app-bg)' }}>
      <AppRouter />
      <Toaster
        theme={isDark ? 'dark' : 'light'}
        position="bottom-right"
        richColors
        closeButton
        toastOptions={{
          style: {
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
            fontFamily: '"DM Sans", sans-serif',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ProjectProvider>
            <PermissionProvider>
              <TimeBarProvider>
                <SafetyAlertProvider>
                  <ThemedApp />
                </SafetyAlertProvider>
              </TimeBarProvider>
            </PermissionProvider>
          </ProjectProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
